import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injectable,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    IPackage,
    IProcess,
    IProcessVersion,
    ODataResponse,
} from '@models';
import {
    GRID_SERVICE,
    IGridSource,
    ODataGridComponent,
} from '@pages/generics/grid';
import {
    ModalService,
    ODataQueryOptions,
    PackageService,
    ProcessService,
} from '@services';
import { UiGridComponent } from '@uipath/angular/components/ui-grid';

import sortBy from 'lodash-es/sortBy';
import {
    finalize,
    takeUntil,
} from 'rxjs/operators';

@Injectable()
export class ProcessVersionSource implements IGridSource<ODataQueryOptions, ODataResponse<IPackage>> {
    public release!: IProcess;

    public get mapper() {
        return this._packageService.mapper;
    }

    constructor(
        private _packageService: PackageService,
    ) { }

    public get(qo: ODataQueryOptions) {
        return this._packageService.getVersions(this.release.processKey, qo);
    }
}

@Component({
    selector: 'ui-process-versions-grid',
    templateUrl: './versions.grid.html',
    styleUrls: ['./versions.grid.scss'],
    providers: [
        ProcessService,
        PackageService,
        {
            provide: GRID_SERVICE,
            useClass: ProcessVersionSource,
            deps: [
                PackageService,
            ],
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class ProcessVersionsGridComponent
    extends ODataGridComponent<IPackage, string>
    implements OnInit {

    @Input()
    public release!: IProcess;

    @Output()
    public update = new EventEmitter<IProcess[]>();

    public isUpdatedToLatestVersion?: boolean;
    public isFirstVersion?: boolean;
    public isProcessAssociated = true;

    @ViewChild(UiGridComponent, { static: false })
    protected _grid!: UiGridComponent<IPackage>;

    private _processService = this._injector.get<ProcessService>(ProcessService);
    private _modalService = this._injector.get<ModalService>(ModalService);

    ngOnInit() {
        (this._service as ProcessVersionSource).release = this.release;

        this._filters$.next([{
            method: 'contains',
            property: 'key',
            value: this.release.processKey,
        }]);

        this.data$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(
                (data) => this._onRefreshVersionItems(data),
            );
    }

    public processVersionIsCurrentVersion(processVersion: IPackage): boolean {
        return this.release.processVersion === processVersion.version;
    }

    public getProcessVersionUsageDate(processVersion: IPackage): Date | null {

        if (!this.release.releaseVersions) {
            return null;
        }

        const orderedReleaseVersions = sortBy(
            this.release.releaseVersions.filter(
                (releaseVersion: IProcessVersion) => releaseVersion.versionNumber === processVersion.version,
            ),
            pv => pv.creationTime,
        );

        return orderedReleaseVersions.length !== 0
            ? orderedReleaseVersions[orderedReleaseVersions.length - 1].creationTime
            : null;
    }

    public onUpdateClick(processVersion: IPackage) {
        const confirmationMessage = this._translate.instant('versions_modal_update_release_version_message', {
            name: this.release.name,
            version: processVersion.version,
        });

        this._modalService.onConfirm(
            processVersion.key,
            () => {
                this.loading$.next(true);
                this._processService
                    .updateReleaseToSpecificVersion(this.release.id, processVersion.version)
                    .pipe(finalize(() => this.loading$.next(false)))
                    .subscribe(this._refreshRelease);

            },
            () => { },
            confirmationMessage,
        );
    }

    public onUpdateToLatestClick() {
        const confirmationMessage =
            this._translate.instant('versions_modal_update_release_latest_version_message', this.release);

        this._modalService.onConfirm(
            this.release.name,
            () => {
                this.loading$.next(true);

                this._processService
                    .updateReleaseToLatestVersion(this.release.id)
                    .pipe(finalize(() => this.loading$.next(false)))
                    .subscribe(this._refreshRelease);

            },
            () => { },
            confirmationMessage,
        );
    }

    public onRollbackToPreviousClick() {
        const confirmationMessage = this._translate
            .instant('versions_modal_update_release_last_version_message', this.release);

        this._modalService
            .onConfirm(
                this.release.name,
                () => {
                    this.loading$.next(true);
                    this._processService
                        .rollbackReleaseToPreviousVersion(this.release.id)
                        .pipe(finalize(() => this.loading$.next(false)))
                        .subscribe(this._refreshRelease);

                },
                () => { },
                confirmationMessage,
            );
    }

    private _refreshRelease = async () => {
        const release = await this._processService
            .getInstance(this.release.id, ['ReleaseVersions'])
            .toPromise();

        // TODO: investigate if we can do this better with the new mapper
        if (release.arguments && release.arguments.input) {
            release.arguments.input
                .forEach(inputArg => inputArg.source = inputArg.hasDefault ? 'package' : null);
        }

        this.update.emit([release]);
        this._grid.refresh.next();
    }

    private _onRefreshVersionItems(data: IPackage[]) {
        const latestVersion: IPackage | undefined = data.find((a: IPackage) => a.isLatestVersion);

        this.isUpdatedToLatestVersion = !!latestVersion && latestVersion.version === this.release.processVersion;
        this.isFirstVersion = this.release.releaseVersions.length === 1;
        this.isProcessAssociated = data.length !== 0;
    }
}
